import { Button } from '@/components/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/form'
import { Input } from '@/components/input'
import { cn } from '@/lib/utils'
import { PasswordInput } from '@frontend/components/enhanced/password-input'
import {
  confirmPasswordSchema,
  generatePasswordRequirements,
  redirectToAuth,
  useLoginMutation,
  useLogoutMutation,
  useRefreshTokenQuery,
  useWhoAmIQuery
} from '@frontend/core/lib/modules/authentication'
import { IMAGE_PRESETS } from '@frontend/core/lib/modules/images'
import { StaticAssetImage } from '@frontend/react'
import { Avatar, AvatarFallback } from '@frontend/ui'
import { getRedirectUri } from '@frontend/utils'
import {
  CheckCircle,
  CheckCircle2,
  CheckCircleIcon,
  Eye,
  EyeOff,
  Loader,
  XCircle
} from 'lucide-react'
import React, { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { z } from 'zod'
import { useOrganisationsData } from '../../domains/organisations/hooks'
import { useAppSelector } from '../../store/hooks'
import { requiredResolver } from '../../utils/form-resolver'

export const AuthGuard = ({ children }: { children: React.ReactNode }) => {
  const authenticated = useAppSelector(
    (state) => state.authentication.authenticated
  )
  const auth = useAppSelector((state) => state.servers.urls.auth)

  const {
    isUninitialized: isRefreshTokenUninitialized,
    isLoading: isRefreshTokenLoading,
    isError: isRefreshTokenError
  } = useRefreshTokenQuery()

  useEffect(() => {
    if (!auth.url || auth?.url === '') {
      console.warn('Auth URL is not configured')
      return
    }
    if (!authenticated && !isRefreshTokenLoading) {
      redirectToAuth(auth.url)
    }
  }, [authenticated, isRefreshTokenLoading, auth?.url])

  if (isRefreshTokenUninitialized || isRefreshTokenLoading) {
    //TODO replace with skeleton of app
    return (
      <div className='bg-background flex min-h-screen items-center justify-center p-4'>
        <div className='text-center'>
          <div className='border-primary mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2'></div>
          <p className='text-muted-foreground'>Checking authentication...</p>
        </div>
      </div>
    )
  }

  // Redirecting state (will redirect in useEffect above)
  if (!authenticated) {
    return (
      <div className='bg-background flex min-h-screen items-center justify-center p-4'>
        <div className='text-center'>
          <div className='border-primary mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2'></div>
          <p className='text-muted-foreground'>Redirecting to login...</p>
        </div>
      </div>
    )
  }

  return children
}

const loginFormSchema = z.object({
  email: z.email(),
  password: z.string()
})

export function LoginForm({ className }: React.ComponentProps<'form'>) {
  const navigate = useNavigate()
  const [showPassword, setShowPassword] = useState(false)

  const [
    login,
    { isLoading: isLoginLoading, error: loginErrorData, isError: isLoginError }
  ] = useLoginMutation()

  const form = useForm<z.infer<typeof loginFormSchema>>({
    resolver: requiredResolver(loginFormSchema),
    defaultValues: {
      email: '',
      password: ''
    }
  })

  const onSubmit = async (values: z.infer<typeof loginFormSchema>) => {
    const { email, password } = values

    await login({
      email,
      password
    })
  }

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  const handleForgotPasswordClick = () => {
    navigate('forgot-password')
  }

  return (
    <Form {...form}>
      <form
        id={'login-form'}
        name='login-form'
        onSubmit={form.handleSubmit(onSubmit)}
        className={cn('flex flex-col gap-6', className)}
        autoComplete='on'
      >
        <div className='flex flex-col items-center gap-2 text-center'>
          <h1 className='text-2xl font-bold'>Login to your account</h1>
          <p className='text-muted-foreground text-sm text-balance'>
            Enter your email below to login to your account
          </p>
        </div>
        <div className='grid gap-6'>
          <div className='grid gap-3'>
            <FormField
              control={form.control}
              name='email'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input placeholder='' {...field} autoComplete='username' />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className='grid gap-3'>
            <div className='items-center'>
              <FormField
                control={form.control}
                name='password'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <div className='relative'>
                      <FormControl>
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          placeholder=''
                          autoComplete='current-password'
                          {...field}
                        />
                      </FormControl>
                      <Button
                        type='button'
                        variant='ghost'
                        size='sm'
                        className='absolute top-0 right-0 h-full px-3 py-2 text-gray-400 hover:text-gray-500'
                        onClick={togglePasswordVisibility}
                        tabIndex={-1}
                      >
                        {showPassword ? (
                          <EyeOff className='h-4 w-4' />
                        ) : (
                          <Eye className='h-4 w-4' />
                        )}
                        <span className='sr-only'>
                          {showPassword ? 'Hide password' : 'Show password'}
                        </span>
                      </Button>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <a
                onClick={handleForgotPasswordClick}
                className='ml-auto cursor-pointer text-xs italic underline-offset-4 hover:underline'
              >
                Forgot your password?
              </a>
            </div>
          </div>
          <Button
            type='submit'
            className='w-full'
            data-cy='login-submit'
            isLoading={isLoginLoading}
          >
            Login
          </Button>
        </div>
        {/* <div className='text-center text-sm'>
          Don&apos;t have an account?{' '}
          <a href='#' className='underline underline-offset-4'>
            Sign up
          </a>
        </div> */}
      </form>
      {loginErrorData && (
        <FormMessage className='text-red-500'>
          {/* TODO: Fix this disgustingness */}
          {'data' in loginErrorData
            ? (loginErrorData.data as any).message
            : 'Login failed. Please try again.'}
        </FormMessage>
      )}
    </Form>
  )
}

export function LoginSuccess() {
  const authenticated = useAppSelector(
    (state) => state.authentication.authenticated
  )

  const { data: user } = useWhoAmIQuery()

  const { filteredData: organisations } = useOrganisationsData({})

  const [logout, { isLoading: isLogoutLoading }] = useLogoutMutation()

  const [showRedirectLoader, setShowRedirectLoader] = useState(false)

  const redirectUri = getRedirectUri()

  useEffect(() => {
    if (authenticated && redirectUri) {
      setShowRedirectLoader(true)
      setTimeout(() => {
        window.location.href = redirectUri
      }, 2000)
    }
  }, [authenticated])

  if (showRedirectLoader) {
    const redirectUri = getRedirectUri()

    return (
      <div className={cn('flex flex-col gap-6')}>
        <div className='flex flex-col items-center gap-2 text-center'>
          <h1 className='text-2xl font-bold'>Login Successful!</h1>
          <p className='text-muted-foreground text-sm text-balance'>
            Logged in as: {user?.email}
          </p>
          <p className='text-muted-foreground text-sm text-balance'>
            Redirecting you back to {redirectUri}...
          </p>
        </div>
        <div className='grid gap-6'>
          <div className='flex justify-center'>
            <div className='flex h-20 w-20 items-center justify-center rounded-full bg-blue-100'>
              <div className='h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600'></div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={cn('flex flex-col gap-6')}>
      <div className='flex flex-col items-center gap-2 text-center'>
        <h1 className='text-2xl font-bold'>Welcome back {user?.firstName}</h1>
        <p className='text-muted-foreground text-sm text-balance'>
          Logged in as: {user?.email}
        </p>
        {organisations && organisations?.length > 0 && (
          <p className='text-muted-foreground text-sm text-balance'>
            Part of {organisations.map((org) => org.name).join(', ')}
          </p>
        )}
      </div>
      <div className='grid gap-6'>
        <div className='flex justify-center'>
          <div className='relative'>
            {/* TODO: Make this a reusable thing for the profile page */}
            <Avatar className='h-20 w-20'>
              <StaticAssetImage
                src={user?.image}
                alt={`${user?.firstName || ''} ${user?.lastName || ''}`}
                preset={IMAGE_PRESETS.playerLarge}
                className='h-20 w-20 rounded-full'
                fallback={
                  <AvatarFallback className='h-20 w-20 text-lg'>
                    {user?.firstName?.[0] || ''}
                    {user?.lastName?.[0] || ''}
                  </AvatarFallback>
                }
              />
            </Avatar>
            <div className='absolute -right-1 -bottom-1 flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-green-100'>
              <CheckCircle className='h-5 w-5 text-green-600' />
            </div>
          </div>
        </div>
        <Button
          onClick={() => logout()}
          className='w-full'
          isLoading={isLogoutLoading}
        >
          Log Out
        </Button>
        {/*<Button*/}
        {/*  onClick={() => console.log('go somewhere')}*/}
        {/*  variant='outline'*/}
        {/*  className='w-full'*/}
        {/*>*/}
        {/*  View Profile*/}
        {/*</Button> */}
        {/*<div className='text-center'>*/}
        {/*  Need help?{' '}*/}
        {/*  <a href='#' className='text-sm underline-offset-4 hover:underline'>*/}
        {/*    Contact Support*/}
        {/*  </a>*/}
        {/*</div>*/}
      </div>
    </div>
  )
}

const forgotPasswordSchema = z.object({
  email: z.email()
})

export type ForgotPasswordPayload = z.infer<typeof forgotPasswordSchema>

export function ForgotPasswordForm({
  onSubmit,
  className,
  errorMessage,
  isLoading
}: Omit<React.ComponentProps<'form'>, 'onSubmit'> & {
  onSubmit: (values: ForgotPasswordPayload) => void
  isLoading: boolean
  errorMessage?: string
}) {
  const navigate = useNavigate()

  const form = useForm<ForgotPasswordPayload>({
    resolver: requiredResolver(forgotPasswordSchema),
    defaultValues: {
      email: ''
    },
    values: { email: '' }
  })

  const goToLogin = () => {
    navigate('/')
  }

  return (
    <Form {...form}>
      <form
        id={'forgot-password-form'}
        name='forgot-password-form'
        onSubmit={form.handleSubmit(onSubmit)}
        className={cn('flex flex-col gap-6', className)}
        autoComplete='on'
      >
        <div className='flex flex-col items-center gap-2 text-center'>
          <h1 className='text-2xl font-bold'> Forgot Password </h1>
          <p className='text-muted-foreground text-sm text-balance'>
            Enter your account email
          </p>
        </div>
        {errorMessage && (
          <div className='rounded-md border border-red-200 bg-red-50 p-4'>
            <div className='flex'>
              <div className='text-sm text-red-700'>{errorMessage}</div>
            </div>
          </div>
        )}
        <div className='grid gap-6'>
          <div className='grid gap-2'>
            <FormField
              control={form.control}
              name='email'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      placeholder='Email Address'
                      {...field}
                      autoComplete='email'
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className='flex w-full flex-row justify-start'>
              <a
                onClick={goToLogin}
                className='cursor-pointer text-xs italic underline-offset-4 hover:underline'
              >
                Login Instead?
              </a>
            </div>
          </div>

          <Button type='submit' className='w-full'>
            {isLoading ? <Loader className='animate-spin' /> : 'Reset Email'}
          </Button>
        </div>
      </form>
    </Form>
  )
}

interface PasswordRequirementsProps {
  password: string
  additionalRequirements: { name: string; isValid: boolean }[]
}

const PasswordRequirements = ({
  password,
  additionalRequirements
}: PasswordRequirementsProps) => {
  const requirements = useMemo(() => {
    return [
      ...generatePasswordRequirements(password),
      ...additionalRequirements
    ]
  }, [password, additionalRequirements])

  return (
    <div className='flex flex-col gap-3 rounded-lg border bg-gray-50 p-4 text-xs'>
      <span className='font-semibold text-gray-700'>Password must:</span>
      <ul className='flex flex-col gap-2'>
        {requirements.map((req, index) => (
          <li key={index} className='flex items-center gap-3'>
            {req.isValid ? (
              <CheckCircle2 size={16} className='text-green-500' />
            ) : (
              <XCircle size={16} className='text-red-500' />
            )}
            <p
              className={
                req.isValid
                  ? 'truncate text-green-600'
                  : 'truncate text-gray-600'
              }
            >
              {req.name}
            </p>
          </li>
        ))}
      </ul>
    </div>
  )
}

export type ConfirmPasswordPayload = z.infer<typeof confirmPasswordSchema>

export function ConfirmPasswordForm({
  onSubmit,
  className,
  errorMessage,
  isLoading,
  title = 'Reset Password',
  subTitle = 'Confirm your new password'
}: Omit<React.ComponentProps<'form'>, 'onSubmit'> & {
  subTitle?: string
  description?: string
  onSubmit: (values: ConfirmPasswordPayload) => void
  isLoading: boolean
  errorMessage?: string
}) {
  const form = useForm<ConfirmPasswordPayload>({
    resolver: requiredResolver(confirmPasswordSchema),
    defaultValues: {
      password: '',
      confirmPassword: ''
    },
    values: { password: '', confirmPassword: '' }
  })

  const password = form.watch('password')
  const confirmPassword = form.watch('confirmPassword')

  return (
    <Form {...form}>
      <form
        id={'forgot-password-form'}
        name='forgot-password-form'
        onSubmit={form.handleSubmit(onSubmit)}
        className={cn('flex flex-col gap-6', className)}
        autoComplete='on'
      >
        <div className='flex flex-col items-center gap-2 text-center'>
          <h1 className='text-2xl font-bold'> {title} </h1>
          <p className='text-muted-foreground text-sm text-balance'>
            {subTitle}
          </p>
        </div>
        {errorMessage && (
          <div className='rounded-md border border-red-200 bg-red-50 p-4'>
            <div className='flex'>
              <div className='text-sm text-red-700'>{errorMessage}</div>
            </div>
          </div>
        )}
        <div className='grid gap-6'>
          <div className='grid gap-2'>
            <FormField
              control={form.control}
              name='password'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <PasswordInput placeholder='Password' {...field} isForm />
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className='grid gap-2'>
            <FormField
              control={form.control}
              name='confirmPassword'
              render={({ field }) => (
                <PasswordInput
                  placeholder='Confirm Password'
                  {...field}
                  isForm
                />
              )}
            />
          </div>
          <PasswordRequirements
            password={password}
            additionalRequirements={[
              {
                isValid: !!password && password === confirmPassword,
                name: 'Passwords must match'
              }
            ]}
          />

          <Button
            type='submit'
            className='w-full'
            disabled={!form.formState.isValid}
          >
            {isLoading ? <Loader className='animate-spin' /> : 'Set Password'}
          </Button>
        </div>
      </form>
    </Form>
  )
}

const DISABLE_RESEND_BUTTON_TIME_SECONDS = 120

export function ForgotPasswordSuccess({
  handleResendEmail,
  isLoading
}: {
  handleResendEmail: () => void
  isLoading: boolean
}) {
  const [previousResendTime, setPreviousResendTime] = useState<number | null>(
    null
  )
  const [timeRemaining, setTimeRemaining] = useState(0)

  const navigate = useNavigate()

  const goToLogin = () => {
    navigate('/')
  }

  useEffect(() => {
    if (previousResendTime) {
      const countdownInterval = setInterval(() => {
        const elapsedTime = Math.floor((Date.now() - previousResendTime) / 1000)

        let remainingTime = DISABLE_RESEND_BUTTON_TIME_SECONDS - elapsedTime

        if (remainingTime <= 0) {
          remainingTime = 0
          clearInterval(countdownInterval)
          setPreviousResendTime(null)
        }

        setTimeRemaining(remainingTime)
      }, 1000)

      return () => clearInterval(countdownInterval)
    }
  }, [timeRemaining, previousResendTime])

  const canResend = timeRemaining === 0

  return (
    <div className='mx-auto flex max-w-sm flex-col items-center gap-6 rounded-lg bg-white p-6 shadow-lg'>
      <div className='rounded-full bg-green-100 p-3'>
        <CheckCircleIcon className='h-8 w-8 text-green-600' />
      </div>

      <h1 className='text-2xl font-bold text-gray-900'>Check Your Email</h1>

      <div className='text-center'>
        <p className='text-sm text-gray-500'>
          If an account associated with this email address exists, a password
          reset link has been sent to the inbox.
        </p>
        <p className='mt-2 text-xs text-gray-400'>
          If you don't see it, please check your spam or junk folder.
        </p>
      </div>

      <div className='mt-2 flex w-full flex-col gap-3'>
        <Button className='w-full' onClick={goToLogin}>
          Back to Login
        </Button>
        <Button
          className='w-full'
          variant='outline'
          disabled={!canResend}
          onClick={() => {
            setPreviousResendTime(Date.now())
            handleResendEmail()
          }}
        >
          {isLoading ? (
            <Loader className='animate-spin' />
          ) : (
            <span>
              Resend Email{' '}
              <span className='text-xs'>
                {' '}
                {!canResend ? timeRemaining : ''}
              </span>
            </span>
          )}
        </Button>
      </div>
    </div>
  )
}
